import React, { useState } from 'react';
import {
  Drawer, Box, Typography, Avatar, Button, Divider,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  CircularProgress,
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { useAuth } from '../AuthContext';

export default function ProfileDrawer({ open, onClose }) {
  const { user, signOut, deleteAccount } = useAuth();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteAccount();
      // Firebase auth state change will handle redirect to sign-in
    } catch (err) {
      setDeleteError('Something went wrong. Please try again.');
      setDeleting(false);
    }
  };

  if (!user) return null;

  return (
    <>
      <Drawer
        anchor="bottom"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            borderRadius: '16px 16px 0 0',
            bgcolor: '#1a1410',
            borderTop: '1px solid rgba(232,184,109,0.15)',
            px: 3,
            pt: 3,
            pb: 4,
          },
        }}
      >
        {/* Pull handle */}
        <Box sx={{ width: 36, height: 4, bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, mx: 'auto', mb: 3 }} />

        {/* User info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Avatar
            src={user.photoURL}
            alt={user.displayName}
            sx={{ width: 56, height: 56, border: '2px solid rgba(232,184,109,0.3)' }}
          />
          <Box>
            <Typography sx={{ fontWeight: 600, color: '#f5e6c8', fontSize: '1rem' }}>
              {user.displayName || 'Detective'}
            </Typography>
            <Typography sx={{ color: '#7a6a4a', fontSize: '0.8rem' }}>
              {user.email}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.07)', mb: 2 }} />

        <Button
          fullWidth
          variant="outlined"
          startIcon={<LogoutIcon />}
          onClick={() => { onClose(); signOut(); }}
          sx={{
            textTransform: 'none',
            borderColor: 'rgba(232,184,109,0.3)',
            color: '#e8b86d',
            py: 1.1,
            mb: 1.5,
            borderRadius: 1.5,
            '&:hover': { borderColor: '#e8b86d', bgcolor: 'rgba(232,184,109,0.06)' },
          }}
        >
          Sign out
        </Button>

        <Button
          fullWidth
          variant="text"
          startIcon={<DeleteForeverIcon />}
          onClick={() => setConfirmOpen(true)}
          sx={{
            textTransform: 'none',
            color: 'error.main',
            py: 1.1,
            borderRadius: 1.5,
            opacity: 0.7,
            '&:hover': { bgcolor: 'rgba(211,47,47,0.08)', opacity: 1 },
          }}
        >
          Delete my account
        </Button>
      </Drawer>

      {/* Delete confirmation dialog */}
      <Dialog
        open={confirmOpen}
        onClose={() => !deleting && setConfirmOpen(false)}
        PaperProps={{ sx: { bgcolor: '#1a1410', border: '1px solid rgba(232,184,109,0.15)', borderRadius: 2, mx: 2 } }}
      >
        <DialogTitle sx={{ color: '#f5e6c8', fontSize: '1rem', pb: 1 }}>
          Delete account?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: '#9a8870', fontSize: '0.875rem' }}>
            This permanently deletes your account and all ratings, watchlist, and preferences. There is no undo.
          </DialogContentText>
          {deleteError && (
            <Typography sx={{ color: 'error.main', fontSize: '0.82rem', mt: 1.5 }}>
              {deleteError}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            disabled={deleting}
            sx={{ textTransform: 'none', color: '#9a8870' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleting}
            color="error"
            variant="contained"
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ textTransform: 'none', borderRadius: 1.5 }}
          >
            {deleting ? 'Deleting…' : 'Yes, delete everything'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
